import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, FileText, X, Filter, ChevronDown, Download } from 'lucide-react';

/**
 * DIA-2 §2.4 — Diary Audit Report.
 *
 * Admin-only desktop screen at /diary-audit. Mirrors the shape and
 * conventions of ScheduleChanges.jsx so admins have one mental model
 * across both audit surfaces. Adds a CSV export via
 * GET /api/diary/audit-log?export=csv.
 */

const ACTION_COLORS = {
  create:  'bg-green-500/20 text-green-400',
  update:  'bg-blue-500/20 text-blue-400',
  delete:  'bg-red-500/20 text-red-400',
};
const ACTION_LABELS = { create: 'Created', update: 'Updated', delete: 'Deleted' };

// DIA-2 §2.4 — the diary writer only logs `update` and `delete` actions
// (creates are visible on the entry itself). Advertising a "Created"
// filter option that always returns 0 rows would confuse admins, so we
// only offer the two actions that are actually audited.
const AUDITED_ACTIONS = ['update', 'delete'];

const DIARY_ENTRY_TYPES = [
  'note', 'labour', 'delivery', 'weather', 'delay', 'rfi',
  'instruction', 'visit', 'materials', 'capture',
];

function formatUKDate(isoStr) {
  if (!isoStr) return '-';
  try {
    const d = new Date(isoStr);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  } catch { return isoStr.slice(0, 16); }
}

function buildDescription(entry) {
  const who = entry.performed_by_name || 'Unknown';
  const author = entry.author_name || 'Unknown';
  const title = entry.entry_title || `${entry.entry_type || 'entry'} #${(entry.entry_reference || entry.entry_id || '').slice(0, 6)}`;
  const proj = entry.project_ref || '';
  const type = (entry.entry_type || '').replace('_', ' ');

  if (entry.action === 'delete') return `${who} deleted ${type} "${title}"${proj ? ' on ' + proj : ''} (author: ${author})`;
  if (entry.action === 'update') return `${who} updated ${type} "${title}"${proj ? ' on ' + proj : ''} (author: ${author})`;
  if (entry.action === 'create') return `${who} created ${type} "${title}"${proj ? ' on ' + proj : ''}`;
  return `${who} ${entry.action} ${title}`;
}

export default function DiaryAudit() {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [expanded, setExpanded] = useState({});

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [performedBy, setPerformedBy] = useState('all');
  const [projectId, setProjectId] = useState('all');
  const [entryType, setEntryType] = useState('all');
  const [action, setAction] = useState('all');

  useEffect(() => {
    if (!isAdmin) { navigate('/portal'); return; }
    api.get('/staff').then(r => setAllStaff(r.data || [])).catch(() => {});
    api.get('/projects?limit=200').then(r => setAllProjects(r.data || [])).catch(() => {});
  }, [isAdmin, navigate, api]);

  const buildParams = useCallback((offset = 0) => {
    const p = new URLSearchParams();
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo) p.set('date_to', dateTo);
    if (performedBy !== 'all') p.set('performed_by', performedBy);
    if (projectId !== 'all') p.set('project_id', projectId);
    if (entryType !== 'all') p.set('entry_type', entryType);
    if (action !== 'all') p.set('action', action);
    p.set('limit', '100');
    p.set('offset', String(offset));
    return p.toString();
  }, [dateFrom, dateTo, performedBy, projectId, entryType, action]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/diary/audit-log?${buildParams(0)}`);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  }, [api, buildParams]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await api.get(`/diary/audit-log?${buildParams(entries.length)}`);
      setEntries(prev => [...prev, ...(res.data.entries || [])]);
    } catch { toast.error('Failed to load more'); }
    finally { setLoadingMore(false); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      // Reuse the current filter params, drop pagination — CSV streams ALL matches.
      const p = new URLSearchParams();
      if (dateFrom) p.set('date_from', dateFrom);
      if (dateTo) p.set('date_to', dateTo);
      if (performedBy !== 'all') p.set('performed_by', performedBy);
      if (projectId !== 'all') p.set('project_id', projectId);
      if (entryType !== 'all') p.set('entry_type', entryType);
      if (action !== 'all') p.set('action', action);
      p.set('export', 'csv');
      // Force axios to give us a Blob so the browser can save it.
      const res = await api.get(`/diary/audit-log?${p.toString()}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Prefer the filename the backend advertises via Content-Disposition
      // so we stay consistent with the spec (`diary-audit-YYYYMMDD-HHMMSS.csv`)
      // and any changes to the naming happen in one place.
      const disp = res.headers?.['content-disposition'] || '';
      const match = disp.match(/filename\s*=\s*"?([^";]+)"?/i);
      a.download = match ? match[1] : `diary-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const clearFilters = () => {
    setDateFrom(''); setDateTo(''); setPerformedBy('all');
    setProjectId('all'); setEntryType('all'); setAction('all');
  };
  const hasFilters = dateFrom || dateTo || performedBy !== 'all' || projectId !== 'all' || entryType !== 'all' || action !== 'all';

  // Restrict `performed_by` options to admins + surveyors — the only two
  // roles that can produce diary audit rows.
  const auditors = allStaff.filter(e => ['admin', 'surveyor'].includes(e.role));

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="diary-audit-page">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#F5A623]" /> Diary Audit
          </h1>
          <p className="text-sm text-gray-400" data-testid="diary-audit-total">
            {total} audit entr{total !== 1 ? 'ies' : 'y'}
          </p>
        </div>
        <Button
          onClick={exportCsv}
          disabled={exporting || loading}
          className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
          data-testid="diary-audit-export-csv-btn"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6 items-end" data-testid="diary-audit-filters">
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger data-testid="diary-audit-filter-action" className="w-[140px] bg-[#2A3240] border-white/20 text-white text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white">All Actions</SelectItem>
            {AUDITED_ACTIONS.map(a => (
              <SelectItem key={a} value={a} className="text-white">{ACTION_LABELS[a]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={entryType} onValueChange={setEntryType}>
          <SelectTrigger data-testid="diary-audit-filter-entry-type" className="w-[140px] bg-[#2A3240] border-white/20 text-white text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10 max-h-72">
            <SelectItem value="all" className="text-white">All Types</SelectItem>
            {DIARY_ENTRY_TYPES.map(t => (
              <SelectItem key={t} value={t} className="text-white capitalize">{t.replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={performedBy} onValueChange={setPerformedBy}>
          <SelectTrigger data-testid="diary-audit-filter-performed-by" className="w-[160px] bg-[#2A3240] border-white/20 text-white text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10 max-h-72">
            <SelectItem value="all" className="text-white">All Users</SelectItem>
            {auditors.map(e => (
              <SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger data-testid="diary-audit-filter-project" className="w-[180px] bg-[#2A3240] border-white/20 text-white text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10 max-h-72">
            <SelectItem value="all" className="text-white">All Projects</SelectItem>
            {allProjects.map(p => (
              <SelectItem key={p.id} value={p.id} className="text-white">{p.project_ref || p.client_name || p.id.slice(0, 8)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="w-[130px] bg-[#2A3240] border-white/20 text-white text-xs h-9"
          data-testid="diary-audit-filter-date-from"
        />
        <Input
          type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="w-[130px] bg-[#2A3240] border-white/20 text-white text-xs h-9"
          data-testid="diary-audit-filter-date-to"
        />
        {hasFilters && (
          <button onClick={clearFilters} className="text-xs text-[#F5A623] hover:underline flex items-center gap-1" data-testid="diary-audit-clear-filters">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16" data-testid="diary-audit-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" />
        </div>
      ) : entries.length === 0 ? (
        <Card className="bg-[#2A3240] border-white/10" data-testid="diary-audit-empty">
          <CardContent className="py-12 text-center">
            {hasFilters ? (
              <>
                <Filter className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No entries match these filters</p>
                <button onClick={clearFilters} className="text-[#F5A623] text-sm mt-2 hover:underline">Clear filters</button>
              </>
            ) : (
              <>
                <FileText className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No diary changes recorded yet</p>
                <p className="text-gray-500 text-xs mt-1">Edits and deletions of diary entries will appear here</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="diary-audit-list">
          {entries.map(entry => (
            <Card key={entry.id} className="bg-[#2A3240] border-white/5" data-testid={`diary-audit-row-${entry.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Badge className={`${ACTION_COLORS[entry.action] || 'bg-gray-500/20 text-gray-400'} text-[10px] flex-shrink-0 mt-0.5`}>
                    {ACTION_LABELS[entry.action] || entry.action}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm">{buildDescription(entry)}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                      <span>{entry.performed_by_name} ({entry.performed_by_role})</span>
                      <Badge className="bg-gray-500/20 text-gray-400 text-[9px]">
                        {entry.view_context === 'portal' ? 'Portal' : 'Admin'}
                      </Badge>
                      <span>{formatUKDate(entry.created_at)}</span>
                    </div>
                    {(entry.action === 'update' || entry.action === 'delete') && (entry.previous_values || entry.new_values) && (
                      <button
                        onClick={() => setExpanded(prev => ({ ...prev, [entry.id]: !prev[entry.id] }))}
                        className="text-[#F5A623] text-xs mt-2 flex items-center gap-1 hover:underline"
                        data-testid={`diary-audit-row-toggle-${entry.id}`}
                      >
                        <ChevronDown className={`w-3 h-3 transition-transform ${expanded[entry.id] ? 'rotate-180' : ''}`} />
                        {expanded[entry.id] ? 'Hide' : 'Show'} details
                      </button>
                    )}
                    {expanded[entry.id] && (
                      <div className="mt-2 text-xs bg-[#1E2530] p-3 rounded border border-white/10 space-y-2 font-mono">
                        {Array.isArray(entry.changed_fields) && entry.changed_fields.length > 0 && (
                          <div>
                            <span className="text-gray-400">Changed fields: </span>
                            <span className="text-gray-300">{entry.changed_fields.join(', ')}</span>
                          </div>
                        )}
                        {entry.previous_values && (
                          <div>
                            <div className="text-gray-400 mb-1">Before:</div>
                            <pre className="text-gray-300 whitespace-pre-wrap break-all text-[11px]">{JSON.stringify(entry.previous_values, null, 2)}</pre>
                          </div>
                        )}
                        {entry.new_values && (
                          <div>
                            <div className="text-gray-400 mb-1">After:</div>
                            <pre className="text-gray-300 whitespace-pre-wrap break-all text-[11px]">{JSON.stringify(entry.new_values, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {entries.length < total && (
            <Button
              onClick={loadMore} disabled={loadingMore} variant="outline"
              className="w-full border-white/10 text-gray-400 hover:bg-white/5"
              data-testid="diary-audit-load-more-btn"
            >
              {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Load more ({total - entries.length} remaining)
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
